import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {Modal as BSModal} from 'bootstrap';
import {Button, ButtonGroup} from 'react-bootstrap';
import {BaseComponent} from '../../../components/BaseComponent';
import useMachineAssignment from '../hooks/useMachineAssignment';
import enumList from '../../../utils/enumList';
import generateRandomID from '../../../utils/generateRandomID';
import AlertModal from '../../../components/modal/AlertModal';
import useAlertModal from '../../../components/modal/hooks/useAlertModal';
import MachineAssignmentEditModal from './MachineAssignmentEditModal';
import MachineAssignmentCreateModal from './MachineAssignmentCreateModal';
import MachineAssignmentList from './MachineAssignmeintList';
import {useDispatch} from 'react-redux';
import {showMessage} from '../../../utils/modalHelper';
const {
  moduleEnums,
  accessModeEnums,
  alertModalModeEnums,
  messageModalMode: messageModalModeEnums,
} = enumList;

const MachineAssignmentListModal = (props, ref) => {
  const {modalRef, customerData, setCustomerData} = props;

  const dispatch = useDispatch();

  const machineIDState = useState('');
  const [assignments, setAssignments] = useState([]);
  const [checkedAssignmentIDs, setCheckedAssignmentIDs] = useState([]);
  const [machineID, setMachineID] = machineIDState;
  const {alertRef, alertModalInstance: alertModalObjRef} = useAlertModal();
  const baseComponent = new BaseComponent(props);
  const {fetchAssignments, deassign} = useMachineAssignment({
    customerCode: customerData.customerCode,
    dispatch,
  });
  const pendingDelRef = useRef([]);
  const checkAllHTMLID = useRef(`machine-assignment-${generateRandomID(20)}`);
  const createModalRef = useRef();
  const createModalObjRef = useRef();
  const editModalRef = useRef();
  const editModalObjRef = useRef();
  const dtableRef = useRef(null);
  /** @type {React.MutableRefObject<Map<number, string>>} */
  const checkIDs = useRef(new Map());
  const hasPermission = baseComponent.checkPermission(
    moduleEnums.customer,
    accessModeEnums.update,
  );

  const executeDeassign = async () => {
    let res = {};
    try {
      res = await deassign(checkedAssignmentIDs);
      showMessage(messageModalModeEnums.info, '解除綁定完成');
    } catch (error) {
      console.error(error);
      showMessage(messageModalModeEnums.error, res);
    } finally {
      clearSelection();
      modalRef.current.show();
      await getAssignments();
    }
  };

  const toggleCreateModal = (type) => {
    if (!(createModalRef.current instanceof HTMLDivElement)) {
      return;
    }
    switch (type) {
      case 'show':
        return createModalObjRef.current.show();
      case 'hide':
        return createModalObjRef.current.hide();
      default:
        return createModalObjRef.current.toggle();
    }
  };

  const toggleEditModal = (type) => {
    if (!(editModalRef.current instanceof HTMLDivElement)) {
      return;
    }
    switch (type) {
      case 'show':
        return editModalObjRef.current.show();
      case 'hide':
        return editModalObjRef.current.hide();
      default:
        return editModalObjRef.current.toggle();
    }
  };

  const clearSelection = () => {
    setCheckedAssignmentIDs([]);
    pendingDelRef.current = [];
    alertModalObjRef.current.hide();
    if (alertModalObjRef.current instanceof BSModal) {
      alertModalObjRef.current.hide();
    }
  };

  const showAlertModal = () => {
    if (!(alertRef.current instanceof HTMLElement)) {
      return;
    }

    if (alertModalObjRef.current instanceof BSModal) {
      modalRef.current.hide();
      alertModalObjRef.current.show();
    }
  };

  const handleBatchDeleteClick = () => {
    if (!checkedAssignmentIDs.length) {
      return;
    }
    pendingDelRef.current = [...checkedAssignmentIDs];
    // modalRef.current.hide();
    showAlertModal();
  };

  const handleCreateClick = () => {
    setCheckedAssignmentIDs([]);
    modalRef.current.hide();
    toggleCreateModal('show');
  };

  const getAssignments = async () => {
    dtableRef.current.dt().processing(true);
    checkIDs.current.clear();
    const assignments = await fetchAssignments();
    setAssignments(assignments);
    dtableRef.current.dt().processing(false);
  };

  useEffect(() => {
    if (!customerData?.customerCode) {
      return;
    }
    getAssignments();
    return () => {
      checkIDs.current.clear();
    };
  }, [customerData]);

  useEffect(() => {
    if (!(ref.current instanceof HTMLDivElement) || !checkAllHTMLID.current) {
      return;
    }
    const element = ref.current.querySelector(`#${checkAllHTMLID.current}`);
    if (!(element instanceof HTMLInputElement)) {
      return;
    }

    const checkHandler = (event) => {
      console.log(checkedAssignmentIDs);
      /** @type {HTMLInputElement} */
      const currentEl = event.target;
      if (!currentEl.classList.contains('machine-assignment-checks')) {
        return;
      }
      const currentElID = parseInt(currentEl.dataset.id);
      if (currentEl.checked) {
        setCheckedAssignmentIDs([...checkedAssignmentIDs, currentElID].sort());
      } else {
        setCheckedAssignmentIDs(
          checkedAssignmentIDs.filter((id) => id !== currentElID),
        );
      }
    };

    const checkAllConf = (e) => {
      if (e.target !== element) {
        return;
      }
      setCheckedAssignmentIDs(
        e.target.checked ? assignments.map(({machineId}) => machineId) : [],
      );
      if (!e.target.checked) {
        checkIDs.current.values().forEach((ref) => {
          ref.current.checked = false;
        });
      }
    };

    ref.current.addEventListener('change', checkAllConf);
    ref.current.addEventListener('change', checkHandler);
    return () => {
      if (!(ref.current instanceof HTMLDivElement) || !checkAllHTMLID.current) {
        return;
      }
      const element = ref.current.querySelector(`#${checkAllHTMLID.current}`);
      if (!(element instanceof HTMLInputElement)) {
        return;
      }
      ref.current.removeEventListener('change', checkAllConf);
      ref.current.removeEventListener('change', checkHandler);
    };
  }, [assignments, checkedAssignmentIDs]);

  useEffect(() => {
    if (checkedAssignmentIDs.length) {
      [...checkIDs.current.entries()].forEach(([id, ref]) => {
        ref.current.checked = checkedAssignmentIDs.includes(id);
      });
      ref.current.querySelector(`#${checkAllHTMLID.current}`).checked =
        assignments.length &&
        checkedAssignmentIDs.length === assignments.length;
    } else {
      ref.current.querySelector(`#${checkAllHTMLID.current}`).checked = false;
    }
  }, [checkedAssignmentIDs]);

  useEffect(() => {
    if (!(ref.current instanceof HTMLDivElement)) {
      return;
    }
    modalRef.current = new BSModal(ref.current);
    return () => {
      if (!(ref.current instanceof HTMLDivElement)) {
        return;
      }
      modalRef.current = undefined;
    };
  }, []);

  return (
    <React.Fragment>
      <div
        ref={ref}
        className="modal fade"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex="-1"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal__title__text">
                {customerData.customerCode}已綁定機器清單
              </div>
              <button
                type="button"
                className="btn-close btn-cancel"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => {
                  setCustomerData({});
                  document.querySelector(
                    `#${checkAllHTMLID.current}`,
                  ).checked = false;
                }}
              ></button>
            </div>
            <div className="modal-body datatable-container">
              <div className="datatable__tool">
                <div className="d-flex align-items-center gap-2">
                  <div className="datatable__tool__label">
                    多筆選擇
                    <div id="checked-count-display"></div>
                  </div>
                  {hasPermission && (
                    <ButtonGroup size="sm">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleBatchDeleteClick}
                      >
                        解除綁定
                      </Button>
                    </ButtonGroup>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {hasPermission && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCreateClick}
                    >
                      <svg className="btn__icon me-2" aria-hidden="true">
                        <use href="#ico-plus-square"></use>
                      </svg>
                      新增
                    </Button>
                  )}
                </div>
              </div>
              <div className="datatable__data">
                <MachineAssignmentList
                  setCheckedAssignmentIDs={setCheckedAssignmentIDs}
                  checkAllHTMLID={checkAllHTMLID}
                  assignments={assignments}
                  checkIDs={checkIDs}
                  showAlertModal={showAlertModal}
                  setMachineID={setMachineID}
                  toggleEditModal={toggleEditModal}
                  hasPermission={hasPermission}
                  modalRef={modalRef}
                  dtableRef={dtableRef}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <MachineAssignmentCreateModal
        ref={createModalRef}
        {...props}
        getAssignments={getAssignments}
        customerData={customerData}
        modalRef={createModalObjRef}
        listModalRef={modalRef}
      />
      <MachineAssignmentEditModal
        ref={editModalRef}
        {...props}
        customerData={customerData}
        machineID={machineID}
        setMachineID={setMachineID}
        getAssignments={getAssignments}
        modalRef={editModalObjRef}
        listModalRef={modalRef}
      />
      <AlertModal
        ref={alertRef}
        mode={alertModalModeEnums.delete}
        title="是否解除綁定"
        onConfirm={executeDeassign}
        onCancel={clearSelection}
      />
    </React.Fragment>
  );
};

export default forwardRef(MachineAssignmentListModal);
